/* eslint-disable react-refresh/only-export-components */
import { useEffect, useMemo, useState } from 'react'
import { Check, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { fetchWithAuth } from '@/lib/api'
import { cn } from '@/lib/utils'

export type KhWorkspaceMember = {
  id: string
  email: string
  full_name?: string | null
  /** Verified address linked to this workspace. Falls back to the account email. */
  notify_email?: string | null
}

function normEmail(email: string): string {
  return email.trim().toLowerCase()
}

/** Address stored on approvers and readers: workspace-linked email when set. */
export function khMemberContactEmail(member: KhWorkspaceMember): string {
  return String(member.notify_email || member.email || '').trim()
}

function memberIdentityEmails(member: KhWorkspaceMember): string[] {
  return [member.notify_email, member.email]
    .map((email) => (email ? normEmail(email) : ''))
    .filter(Boolean)
}

function memberLabel(member: KhWorkspaceMember): string {
  const name = typeof member.full_name === 'string' ? member.full_name.trim() : ''
  return name || member.email
}

function initialFor(label: string): string {
  const ch = label.trim().charAt(0)
  return ch ? ch.toUpperCase() : '?'
}

function sameEmailList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const left = a.map(normEmail).sort()
  const right = b.map(normEmail).sort()
  return left.every((email, i) => email === right[i])
}

/** Workspace members for the document's organization. Contact email is the workspace-linked address. */
export function useKhWorkspaceMembers(organizationId: string | undefined) {
  const [members, setMembers] = useState<KhWorkspaceMember[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organizationId) {
      setMembers([])
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetchWithAuth(`/api/organizations/${organizationId}/members`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Could not load workspace members')
        const rows = (await res.json()) as KhWorkspaceMember[]
        if (!cancelled) setMembers(Array.isArray(rows) ? rows : [])
      })
      .catch((err) => {
        if (!cancelled) {
          setMembers([])
          setError(err instanceof Error ? err.message : 'Could not load workspace members')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [organizationId])

  return { members, loading, error }
}

export function KnowledgeHubPeoplePicker({
  label,
  emails,
  members,
  membersLoading,
  membersError,
  disabled,
  testId,
  onChange,
}: {
  label: string
  emails: string[]
  members: KhWorkspaceMember[]
  membersLoading: boolean
  membersError: string | null
  disabled?: boolean
  testId: string
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(emails)
  const [query, setQuery] = useState('')

  const selected = open ? draft : emails

  const byEmail = useMemo(() => {
    const map = new Map<string, KhWorkspaceMember>()
    for (const member of members) {
      for (const email of memberIdentityEmails(member)) {
        map.set(email, member)
      }
    }
    return map
  }, [members])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = [...members].sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)))
    if (!q) return rows
    return rows.filter((member) => {
      const emails = memberIdentityEmails(member)
      return memberLabel(member).toLowerCase().includes(q) || emails.some((email) => email.includes(q))
    })
  }, [members, query])

  function labelForEmail(email: string): string {
    return memberLabel(byEmail.get(normEmail(email)) || { id: email, email })
  }

  function commit(next: string[]) {
    if (sameEmailList(next, emails)) return
    onChange(next)
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setDraft(emails)
      setQuery('')
    } else {
      commit(draft)
    }
    setOpen(nextOpen)
  }

  function toggleMember(email: string) {
    const key = normEmail(email)
    setDraft((prev) => {
      if (prev.some((item) => normEmail(item) === key)) {
        return prev.filter((item) => normEmail(item) !== key)
      }
      return [...prev, email]
    })
  }

  function removeEmail(email: string) {
    const key = normEmail(email)
    const next = selected.filter((item) => normEmail(item) !== key)
    if (open) {
      setDraft(next)
      return
    }
    commit(next)
  }

  return (
    <div className="space-y-1.5 min-w-0" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.map((email) => {
          const name = labelForEmail(email)
          return (
            <span
              key={normEmail(email)}
              title={email}
              className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-xs"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                {initialFor(name)}
              </span>
              <span className="truncate">{name}</span>
              <button
                type="button"
                className="rounded-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                aria-label={`Remove ${name}`}
                disabled={disabled}
                onClick={() => removeEmail(email)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )
        })}
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={disabled || (membersLoading && members.length === 0)}
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-1.5" align="start">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people"
              className="h-8 text-xs"
              aria-label={`Search ${label.toLowerCase()}`}
            />
            <div className="mt-1 max-h-48 overflow-auto">
              {membersLoading ? (
                <p className="px-2 py-2 text-xs text-muted-foreground">Loading people...</p>
              ) : membersError ? (
                <p className="px-2 py-2 text-xs text-red-500">{membersError}</p>
              ) : filtered.length === 0 ? (
                <p className="px-2 py-2 text-xs text-muted-foreground">No matching people</p>
              ) : (
                filtered.map((member) => {
                  const email = khMemberContactEmail(member)
                  const picked = selected.some((item) =>
                    memberIdentityEmails(member).includes(normEmail(item))
                  )
                  const name = memberLabel(member)
                  return (
                    <button
                      key={member.id}
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted',
                        picked && 'bg-muted/70',
                      )}
                      onClick={() => toggleMember(email)}
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                        {initialFor(name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{name}</span>
                        {name !== email ? (
                          <span className="block truncate text-muted-foreground">{email}</span>
                        ) : null}
                      </span>
                      {picked ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
                    </button>
                  )
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
